import React, { useEffect, useState } from 'react';
import { Megaphone, Loader2, Save, Trash2, RotateCcw, Plus, ListChecks } from 'lucide-react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const ScheduleBadge = ({ a }) => {
  const today = new Date().toISOString().slice(0, 10);
  let label = 'Live'; let cls = 'bg-emerald-100 text-emerald-700';
  if (!a.active) { label = 'Off'; cls = 'bg-slate-100 text-slate-500'; }
  else if (a.starts_at && a.starts_at > today) { label = `Scheduled · starts ${a.starts_at}`; cls = 'bg-blue-100 text-blue-700'; }
  else if (a.ends_at && a.ends_at < today) { label = 'Expired'; cls = 'bg-rose-100 text-rose-600'; }
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${cls}`} data-testid={`announcement-badge-${a.id}`}>{label}</span>;
};

const STAGE_LABELS = {
  placed: 'Order placed',
  confirmed: 'Store confirmed',
  preparing: 'Preparing',
  ready_for_pickup: 'Ready for pickup',
  on_the_way: 'On the way',
  delivered: 'Delivered',
};

export default function StatusUpdatesPage() {
  const [defaults, setDefaults] = useState({});
  const [messages, setMessages] = useState({});
  const [anns, setAnns] = useState([]);
  const [newText, setNewText] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sm, list] = await Promise.all([adminApi.getStatusMessages(), adminApi.listAnnouncements()]);
      setDefaults(sm.defaults);
      setMessages({ ...sm.defaults, ...sm.overrides });
      setAnns(list);
    } catch (e) { toast.error('Failed to load status updates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveMessages = async () => {
    setSaving(true);
    try {
      const r = await adminApi.saveStatusMessages(messages);
      setMessages({ ...r.defaults, ...r.overrides });
      toast.success('Status messages saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const addAnn = async () => {
    if (!newText.trim()) return;
    if (newStart && newEnd && newEnd < newStart) { toast.error('End date must be on or after the start date'); return; }
    setBusy('new');
    try {
      await adminApi.createAnnouncement({ text: newText.trim(), starts_at: newStart || null, ends_at: newEnd || null });
      setNewText(''); setNewStart(''); setNewEnd('');
      setAnns(await adminApi.listAnnouncements());
      toast.success('Announcement added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setBusy(null); }
  };

  const saveAnn = async (a) => {
    if (a.starts_at && a.ends_at && a.ends_at < a.starts_at) { toast.error('End date must be on or after the start date'); return; }
    setBusy(a.id);
    try {
      await adminApi.updateAnnouncement(a.id, {
        text: a.text,
        active: a.active,
        ...(a.starts_at ? { starts_at: a.starts_at } : { clear_starts_at: true }),
        ...(a.ends_at ? { ends_at: a.ends_at } : { clear_ends_at: true }),
      });
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(null); }
  };

  const deleteAnn = async (id) => {
    setBusy(id);
    try {
      await adminApi.deleteAnnouncement(id);
      setAnns((p) => p.filter((x) => x.id !== id));
      toast.success('Deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="status-updates-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
        <Megaphone className="h-6 w-6 text-indigo-600" /> Status updates
      </h1>
      <p className="text-slate-500 mt-1 text-sm">
        Customers see live status updates every <b>7 seconds</b>. Edit the message for each order stage,
        and manage the rotating announcement ticker shown across the app.
      </p>

      <Card className="mt-6 border-slate-200">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <ListChecks className="h-3.5 w-3.5" /> Order stage messages
          </div>
          <div className="mt-4 space-y-3 max-w-2xl">
            {Object.keys(STAGE_LABELS).map((k) => (
              <div key={k} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-2 items-center">
                <div className="text-sm font-medium text-slate-700">{STAGE_LABELS[k]}</div>
                <Input value={messages[k] || ''} maxLength={200}
                  onChange={(e) => setMessages({ ...messages, [k]: e.target.value })}
                  className="h-9" data-testid={`status-msg-${k}`} />
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-700"
                  title="Reset to default"
                  onClick={() => setMessages({ ...messages, [k]: defaults[k] })}
                  data-testid={`status-msg-reset-${k}`}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button onClick={saveMessages} disabled={saving} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" data-testid="status-msgs-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save messages
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-6 border-slate-200">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <Megaphone className="h-3.5 w-3.5" /> Announcement ticker
          </div>
          <p className="text-xs text-slate-500 mt-1">Active announcements rotate on the app every 7 seconds. Add a start/end date to schedule promotions that appear and expire on their own.</p>

          <div className="mt-4 space-y-2 max-w-3xl">
            <div className="flex gap-2">
              <Input value={newText} onChange={(e) => setNewText(e.target.value)} maxLength={240}
                placeholder="e.g. Free delivery on your first ShipitNow order this weekend!"
                className="h-9" data-testid="announcement-new-input"
                onKeyDown={(e) => e.key === 'Enter' && addAnn()} />
              <Button onClick={addAnn} disabled={busy === 'new' || !newText.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="announcement-add-btn">
                {busy === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add
              </Button>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Starts</span>
              <Input type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} className="h-8 w-40" data-testid="announcement-new-start" />
              <span>Ends</span>
              <Input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="h-8 w-40" data-testid="announcement-new-end" />
              <span className="text-slate-400">(both optional — leave empty to run forever)</span>
            </div>
          </div>

          <div className="mt-5 space-y-4 max-w-3xl">
            {anns.length === 0 && <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl" data-testid="announcements-empty">No announcements yet.</div>}
            {anns.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-100 p-3" data-testid={`announcement-row-${a.id}`}>
                <div className="flex items-center gap-2">
                  <Switch checked={a.active}
                    onCheckedChange={(v) => setAnns((p) => p.map((x) => (x.id === a.id ? { ...x, active: v } : x)))}
                    data-testid={`announcement-active-${a.id}`} />
                  <Input value={a.text} maxLength={240}
                    onChange={(e) => setAnns((p) => p.map((x) => (x.id === a.id ? { ...x, text: e.target.value } : x)))}
                    className={`h-9 ${a.active ? '' : 'opacity-50'}`} data-testid={`announcement-text-${a.id}`} />
                  <Button size="sm" variant="outline" onClick={() => saveAnn(a)} disabled={busy === a.id} data-testid={`announcement-save-${a.id}`}>
                    {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-700" onClick={() => deleteAnn(a.id)} disabled={busy === a.id} data-testid={`announcement-delete-${a.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-2 pl-11 text-xs text-slate-500 flex-wrap">
                  <span>Starts</span>
                  <Input type="date" value={a.starts_at || ''}
                    onChange={(e) => setAnns((p) => p.map((x) => (x.id === a.id ? { ...x, starts_at: e.target.value } : x)))}
                    className="h-8 w-40" data-testid={`announcement-start-${a.id}`} />
                  <span>Ends</span>
                  <Input type="date" value={a.ends_at || ''}
                    onChange={(e) => setAnns((p) => p.map((x) => (x.id === a.id ? { ...x, ends_at: e.target.value } : x)))}
                    className="h-8 w-40" data-testid={`announcement-end-${a.id}`} />
                  <ScheduleBadge a={a} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
